// src/components/ProtectedRoute.tsx
import { UserInfoUserRole, UserInfoUserType } from "@/lib/api/interfaces/user-profile.interface";
import { hasAnyRole, hasRolePermission, parseRole } from "@/lib/utils/role-manager";
import { BlueLoadingFallback } from "@/ui-components/ui/suspense-wrapper";
import React, { ReactNode, useContext } from "react";
import { Context } from "./context";
import { Button } from "@/ui-components/ui/button";
import { AuroraBackground } from "@/ui-components/ui/shadcn-io/aurora-background";
import { Lock, ArrowLeft } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  // Option 1: Single minimum required role (hierarchical)
  minRole?: UserInfoUserRole;
  // Option 2: Array of specific allowed roles (exact match)
  allowedRoles?: UserInfoUserRole[];
  // Option 3: object for min role for internal and customer
  minRoleMap?: {
    internal: UserInfoUserRole;
    customer: UserInfoUserRole;
  };
  // Custom fallback component for unauthorized access
  fallback?: ReactNode;
  // Redirect path for unauthorized users
  redirectTo?: string;
  // Disable default padding (p-8)
  disablePadding?: boolean;
}

// Default unauthorized access component
export const UnauthorizedAccess = () => (
  <div className="h-screen w-full bg-white fixed top-0 left-0 z-50">
    <AuroraBackground variant="red">
      <div className="w-full max-w-2xl px-6 relative z-10">
        <div className="mx-auto flex flex-col items-center text-center">
          <div className="mb-6 grid place-items-center rounded-2xl bg-white/70 p-4 ring-1 ring-black/5 backdrop-blur">
            <Lock className="h-9 w-9 text-red-600" />
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">Zugriff verweigert</h3>
          <p className="text-gray-500 max-w-md mx-auto leading-relaxed">
            Du hast keine Berechtigung, auf diese Seite zuzugreifen. Bitte wende dich an deinen Administrator, wenn du glaubst, dass dies ein Fehler ist.
          </p>

          <div className="flex flex-col sm:flex-row gap-2 justify-center w-full mt-6">
            <Button
              onClick={() => (window.location.href = "/")}
              className="w-full sm:w-auto h-11 px-8 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow-sm transition-all flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Startseite
            </Button>
          </div>
        </div>
      </div>
    </AuroraBackground>
  </div>
);

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  minRole,
  allowedRoles,
  minRoleMap,
  fallback = <UnauthorizedAccess />,
  redirectTo,
  disablePadding = true,
}) => {
  const { user, currentDepartment } = useContext(Context);

  if (!user) {
    return <BlueLoadingFallback text="Lädt Nutzer..." />;
  }

  // Department-Guard: Wenn Customer-User den Modus "generally" hat, nur Home erlauben
  if (user.userType === UserInfoUserType.CUSTOMER_USER && currentDepartment === "generally") {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    if (path !== "/") {
      const target = redirectTo || "/";
      // Hard redirect, um History sauber zu halten
      if (typeof window !== "undefined") {
        window.location.href = target;
      }
      return null;
    }
  }

  // Parse user role from API response
  const userRole = parseRole(user.userRole);

  // If we can't parse the user role, deny access
  if (!userRole) {
    console.warn("Invalid user role:", user.userRole);
    return <>{fallback}</>;
  }

  let hasAccess = false;

  // Check permissions based on provided props
  if (minRole) {
    // Hierarchical permission check
    hasAccess = hasRolePermission(userRole, minRole);
  } else if (allowedRoles && allowedRoles.length > 0) {
    // Specific roles check
    hasAccess = hasAnyRole(userRole, allowedRoles);
  } else if (minRoleMap) {
    if (user.userType === UserInfoUserType.INTERNAL_USER) {
      hasAccess = hasRolePermission(userRole, minRoleMap.internal);
    } else if (user.userType === UserInfoUserType.CUSTOMER_USER) {
      hasAccess = hasRolePermission(userRole, minRoleMap.customer);
    }
  } else {
    // If no role restrictions specified, allow access
    hasAccess = true;
  }

  // Handle unauthorized access
  if (!hasAccess) {
    if (redirectTo) {
      // Redirect logic would go here if using react-router
      window.location.href = redirectTo;
      return null;
    }

    return <>{fallback}</>;
  }

  // User has access, render children
  return (
    <div className={`w-full h-full ${disablePadding ? '' : 'p-8'}`}>
      {children}
    </div>
  );
};

// Convenience components for common role checks
export const AdminOnlyRoute: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback }) => (
  <ProtectedRoute minRole={UserInfoUserRole.ADMIN} fallback={fallback}>
    {children}
  </ProtectedRoute>
);
